import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

// Chat flotante persistente estilo Facebook (feat_269).
// Los chats abiertos y la POSICIÓN son estado EFÍMERO de sesión. El TAMAÑO no:
// es una preferencia de usuario (quien trabaja en 3K lo agranda una vez y no
// quiere volver a hacerlo en cada recarga), así que va a localStorage con el
// mismo patrón y prefijo `waia_` que UiPrefs (`waia_hide_channels`) y DarkMode
// (`waia_dark_mode`).
// Provider montado dentro de UiPrefsProvider → tiene Auth/Settings/WhatsApps disponibles.
const MAX_CHATS = 5;
const SIZE_KEY = "waia_floating_chat_size";

// Sólo saneo de tipo/rango grosero: acotar contra el viewport es trabajo del
// componente (es el único que escucha resize/orientationchange). Acá alcanza con
// rechazar basura y valores absurdos de una pantalla que ya no existe.
const readStoredSize = () => {
  try {
    const raw = localStorage.getItem(SIZE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    const w = Number(v?.w);
    const h = Number(v?.h);
    if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
    if (w < 200 || h < 200 || w > 5000 || h > 5000) return null;
    return { w: Math.round(w), h: Math.round(h) };
  } catch (e) {
    // localStorage puede tirar (modo privado, cookies bloqueadas). El chat
    // funciona igual con el default; no hay nada que avisarle al usuario.
    return null;
  }
};

const FloatingChatContext = createContext({
  chats: [],
  position: null,
  size: null,
  openChat: () => {},
  openChatByPhone: () => {},
  closeChat: () => {},
  minimizeChat: () => {},
  expandChat: () => {},
  setPosition: () => {},
  setSize: () => {},
  persistSize: () => {},
  setChatExpiry: () => {},
});

export const FloatingChatProvider = ({ children }) => {
  const [chats, setChats] = useState([]);
  const [position, setPosition] = useState(null);
  // Tamaño del popup. `setSize` es el estado vivo del arrastre; `persistSize` lo
  // guarda, y se llama UNA vez al soltar el mouse (si escribiera en cada
  // mousemove serían cientos de writes por arrastre). feat_271.
  const [size, setSize] = useState(() => readStoredSize());
  const persistSize = useCallback((next) => {
    setSize(next);
    try {
      localStorage.setItem(SIZE_KEY, JSON.stringify(next));
    } catch (e) {
      /* sin persistencia; el tamaño vive lo que dure la sesión */
    }
  }, []);

  // Agrega/expande un chat. Si ya está en la lista → lo expande.
  // Solo 1 con minimized=false a la vez. Al superar MAX_CHATS → cierra el más antiguo.
  // meta (feat_273): { origin, sessionId, expiresAt } para el modo turnos.
  // meta (feat_282): { whatsappName, whatsappNumber } línea de salida del ticket.
  const openChat = useCallback((ticketId, contactName, meta = {}) => {
    if (!ticketId) return;
    const id = Number(ticketId);
    setChats((prev) => {
      const exists = prev.find((c) => c.ticketId === id);
      if (exists) {
        // Expandir el existente, minimizar el resto. Si trae datos de sesión
        // nuevos (reabierto en modo turnos), refrescarlos.
        return prev.map((c) =>
          c.ticketId === id
            ? {
                ...c,
                minimized: false,
                origin: meta.origin ?? c.origin,
                sessionId: meta.sessionId ?? c.sessionId,
                expiresAt: meta.expiresAt ?? c.expiresAt,
                whatsappName: meta.whatsappName ?? c.whatsappName,
                whatsappNumber: meta.whatsappNumber ?? c.whatsappNumber,
              }
            : { ...c, minimized: true }
        );
      }
      let next = prev.map((c) => ({ ...c, minimized: true }));
      next = [
        ...next,
        {
          ticketId: id,
          contactName: contactName || String(id),
          minimized: false,
          origin: meta.origin,
          sessionId: meta.sessionId,
          expiresAt: meta.expiresAt,
          whatsappName: meta.whatsappName,
          whatsappNumber: meta.whatsappNumber,
        },
      ];
      if (next.length > MAX_CHATS) next = next.slice(next.length - MAX_CHATS);
      return next;
    });
  }, []);

  const openChatByPhone = useCallback(
    async (phone, opts = {}) => {
      if (!phone) return;
      // feat_282: línea específica. Si viene opts.whatsappId, el backend crea/reusa
      // el ticket en ESA línea (pinneado) y valida que sea de la empresa (403 si no).
      // Sin whatsappId → body idéntico al histórico (TurnosIA ya deployó el botón
      // sin este parámetro).
      const body = { phone };
      if (opts.whatsappId !== undefined && opts.whatsappId !== null) {
        body.whatsappId = opts.whatsappId;
      }
      try {
        const { data } = await api.post("/tickets/by-phone", body);
        const waMeta = {
          whatsappName: data.whatsappName,
          whatsappNumber: data.whatsappNumber,
        };
        // Modo turnos (feat_273): abrir una sesión que pausa la IA y permite
        // escribir en tickets pending. Si la sesión falla → abrir en modo normal
        // + aviso (nunca dejar al operador sin poder abrir el chat).
        if (opts.origin === "turnos") {
          try {
            const { data: session } = await api.post("/turnos-chat/sessions", {
              ticketId: data.ticketId,
            });
            openChat(data.ticketId, data.contactName, {
              origin: "turnos",
              sessionId: session.sessionId,
              expiresAt: session.expiresAt,
              ...waMeta,
            });
            return;
          } catch (e) {
            toast.warn(i18n.t("turnosChat.sessionError"));
            openChat(data.ticketId, data.contactName, waMeta);
            return;
          }
        }
        openChat(data.ticketId, data.contactName, waMeta);
      } catch (err) {
        // 403 = whatsappId de otra empresa o inexistente. NO abrir el chat: abrir
        // en otra línea sería justo el bug que este parámetro evita.
        if (err?.response?.status === 403) {
          toast.error(i18n.t("floatingChat.lineNotAllowed"));
        } else {
          toast.error(i18n.t("floatingChat.errorOpening"));
        }
      }
    },
    [openChat]
  );

  // Actualiza la expiración de la sesión de turnos de un chat (extend / refetch).
  const setChatExpiry = useCallback((ticketId, expiresAt) => {
    const id = Number(ticketId);
    setChats((prev) =>
      prev.map((c) => (c.ticketId === id ? { ...c, expiresAt } : c))
    );
  }, []);

  const closeChat = useCallback((ticketId) => {
    const id = Number(ticketId);
    setChats((prev) => prev.filter((c) => c.ticketId !== id));
  }, []);

  const minimizeChat = useCallback((ticketId) => {
    const id = Number(ticketId);
    setChats((prev) =>
      prev.map((c) => (c.ticketId === id ? { ...c, minimized: true } : c))
    );
  }, []);

  const expandChat = useCallback((ticketId) => {
    const id = Number(ticketId);
    setChats((prev) =>
      prev.map((c) => ({ ...c, minimized: c.ticketId !== id }))
    );
  }, []);

  // Listener global: cualquier parte de la app puede abrir un chat flotante
  // disparando window.dispatchEvent(new CustomEvent("waia:open-chat", {detail:{phone}})).
  useEffect(() => {
    const handler = (e) => {
      const phone = e?.detail?.phone;
      if (phone)
        openChatByPhone(phone, {
          origin: e?.detail?.origin,
          whatsappId: e?.detail?.whatsappId,
        });
    };
    window.addEventListener("waia:open-chat", handler);
    return () => window.removeEventListener("waia:open-chat", handler);
  }, [openChatByPhone]);

  return (
    <FloatingChatContext.Provider
      value={{
        chats,
        position,
        size,
        openChat,
        openChatByPhone,
        closeChat,
        minimizeChat,
        expandChat,
        setPosition,
        setSize,
        persistSize,
        setChatExpiry,
      }}
    >
      {children}
    </FloatingChatContext.Provider>
  );
};

export const useFloatingChat = () => useContext(FloatingChatContext);

export default FloatingChatContext;
